import React from 'react';
import {
  Gift,
  MessageSquare,
  Pause,
  Play,
  Share2,
  ShoppingBag,
  SkipBack,
  SkipForward,
} from 'lucide-react';

export default function RadioRecordActions({
  capsule,
  isPlaying = false,
  isLoading = false,
  onTogglePlay,
  onPrevious,
  onNext,
  onFeedback,
  onShare,
  onTip,
  onBuySupport,
}) {
  if (!capsule) return null;

  const tipUnavailableReason =
    capsule.tipUnavailableReason ||
    'Artist support is not set up for this record yet.';
  const buyUnavailableReason =
    capsule.buyUnavailableReason ||
    'Artist support is not set up for this record yet.';
  const canOpenPurchaseStatus =
    capsule.alreadyPurchased === true && !capsule.canBuySupportWithJgk;

  return (
    <div className="jg-radio-dock-actions" aria-label="Radio dock actions">
      <div className="jg-radio-dock-transport">
        <button
          type="button"
          className="jg-radio-dock-icon-btn"
          onClick={onPrevious}
          aria-label="Previous radio item"
        >
          <SkipBack aria-hidden="true" size={17} />
        </button>
        <button
          type="button"
          className="jg-radio-dock-play-btn"
          onClick={onTogglePlay}
          disabled={!capsule.canPlay || isLoading}
          aria-label={isPlaying ? 'Pause radio rotation' : 'Play radio rotation'}
        >
          {isPlaying ? (
            <Pause aria-hidden="true" size={18} />
          ) : (
            <Play aria-hidden="true" size={18} />
          )}
          <span>{isLoading ? 'Loading' : isPlaying ? 'Pause' : 'Play'}</span>
        </button>
        <button
          type="button"
          className="jg-radio-dock-icon-btn"
          onClick={onNext}
          aria-label="Next radio item"
        >
          <SkipForward aria-hidden="true" size={17} />
        </button>
      </div>

      <div className="jg-radio-dock-action-grid">
        {capsule.canGiveFeedback ? (
          <button
            type="button"
            className="jg-radio-dock-action-btn"
            onClick={onFeedback}
            aria-label="Share your honest feedback"
          >
            <MessageSquare aria-hidden="true" size={16} />
            <span>
              Share your honest feedback
            </span>
          </button>
        ) : null}

        {capsule.canShare ? (
          <button
            type="button"
            className="jg-radio-dock-action-btn"
            onClick={onShare}
            aria-label="Share"
          >
            <Share2 aria-hidden="true" size={16} />
            <span>Share</span>
          </button>
        ) : null}

        {capsule.canTipWithJgk ? (
          <button
            type="button"
            className="jg-radio-dock-action-btn"
            onClick={onTip}
            aria-label="Tip with JGK"
          >
            <Gift aria-hidden="true" size={16} />
            <span>
              Tip with JGK <strong>Support artist</strong>
            </span>
          </button>
        ) : capsule.hasJgkSupportFields ? (
          <button
            type="button"
            className="jg-radio-dock-action-btn"
            disabled
            aria-label={`Tip with JGK unavailable: ${tipUnavailableReason}`}
          >
            <Gift aria-hidden="true" size={16} />
            <span>
              Tip with JGK <strong>{tipUnavailableReason}</strong>
            </span>
          </button>
        ) : capsule.tipComingNext ? (
          <button
            type="button"
            className="jg-radio-dock-action-btn"
            disabled
            aria-label="Tip with JGK Coming next"
          >
            <Gift aria-hidden="true" size={16} />
            <span>
              Tip with JGK <strong>Coming next</strong>
            </span>
          </button>
        ) : null}

        {capsule.canBuySupportWithJgk || canOpenPurchaseStatus ? (
          <button
            type="button"
            className="jg-radio-dock-action-btn"
            onClick={onBuySupport}
            aria-label={
              canOpenPurchaseStatus
                ? 'Open Buy or Support purchase status'
                : 'Buy or Support with JGK'
            }
          >
            <ShoppingBag aria-hidden="true" size={16} />
            <span>
              {canOpenPurchaseStatus ? 'Purchased' : 'Buy / Support'}
              <strong>
                {canOpenPurchaseStatus
                  ? capsule.downloadAvailable
                    ? 'Download issued'
                    : 'Download used'
                  : capsule.buyPriceJgk
                    ? `${capsule.buyPriceJgk} JGK`
                    : 'Buy with JGK'}
              </strong>
            </span>
          </button>
        ) : capsule.hasJgkSupportFields ? (
          <button
            type="button"
            className="jg-radio-dock-action-btn"
            disabled
            aria-label={`Buy or Support unavailable: ${buyUnavailableReason}`}
          >
            <ShoppingBag aria-hidden="true" size={16} />
            <span>
              Buy / Support <strong>{buyUnavailableReason}</strong>
            </span>
          </button>
        ) : capsule.buySupportComingNext ? (
          <button
            type="button"
            className="jg-radio-dock-action-btn"
            disabled
            aria-label="Buy or Support Coming next"
          >
            <ShoppingBag aria-hidden="true" size={16} />
            <span>
              Buy / Support <strong>Coming next</strong>
            </span>
          </button>
        ) : null}
      </div>
    </div>
  );
}
